import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Switch,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import { DASHBOARD_CARDS, DashboardCardGroup } from '../dashboardCardsConfig';
import { ReportSpec } from '../reportCatalog';

interface DashboardSettingsModalProps {
  open: boolean;
  onClose: () => void;
  hiddenCards: string[];
  onToggleCard: (cardId: string) => void;
  /** Kullanıcının kurduğu dinamik rapor kartları */
  customReports?: ReportSpec[];
  onAddReport?: () => void;
  onEditReport?: (spec: ReportSpec) => void;
  onDeleteReport?: (spec: ReportSpec) => void;
  /** AI rapor asistanı butonu — backend'de AI yapılandırılmamışsa undefined bırakılır, buton gizlenir */
  onAskAi?: () => void;
}

const GROUP_ORDER: DashboardCardGroup[] = ['stats', 'widget', 'chart'];

const GROUP_TITLE_KEYS: Record<DashboardCardGroup, string> = {
  stats: 'dashboard.cards.groupStats',
  widget: 'dashboard.cards.groupWidgets',
  chart: 'dashboard.cards.groupCharts',
};

/**
 * Dashboard Görünüm Ayarları
 * Kart grupları (İstatistik / Widget / Grafik) yan yana sütunlar halinde listelenir;
 * switch ile aç/kapat, değişiklik anında kaydedilir. Mobilde sütunlar alt alta yığılır.
 */
const DashboardSettingsModal: React.FC<DashboardSettingsModalProps> = ({
  open,
  onClose,
  hiddenCards,
  onToggleCard,
  customReports = [],
  onAddReport,
  onEditReport,
  onDeleteReport,
  onAskAi,
}) => {
  const { t } = useTranslation('accounting');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TuneIcon color="primary" />
          <Typography variant="h6" component="span" fontWeight="bold">
            {t('dashboard.viewSettingsTitle')}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Typography variant="body2" color="textSecondary" sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
          {t('dashboard.viewSettingsDescription')}
        </Typography>
        {/* Gruplar yan yana sütunlar; mobilde (sm altı) alt alta yığılır */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'stretch',
            pb: 1,
          }}
        >
          {GROUP_ORDER.map((group, groupIndex) => {
            const cards = DASHBOARD_CARDS.filter((c) => c.group === group);
            if (cards.length === 0) return null;
            return (
              <Box
                key={group}
                sx={{
                  flex: 1,
                  minWidth: { sm: 220 },
                  borderLeft: { xs: 'none', sm: groupIndex > 0 ? '1px solid' : 'none' },
                  borderTop: { xs: groupIndex > 0 ? '1px solid' : 'none', sm: 'none' },
                  borderColor: { xs: 'divider', sm: 'divider' },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ px: 2, pt: 1.5, display: 'block', fontWeight: 'bold', color: 'text.secondary', textTransform: 'uppercase' }}
                >
                  {t(GROUP_TITLE_KEYS[group])}
                </Typography>
                <List dense disablePadding>
                  {cards.map((card) => {
                    const visible = !hiddenCards.includes(card.id);
                    return (
                      <ListItem
                        key={card.id}
                        secondaryAction={
                          <Switch
                            edge="end"
                            size="small"
                            checked={visible}
                            onChange={() => onToggleCard(card.id)}
                          />
                        }
                        sx={{ py: 0 }}
                      >
                        <ListItemText
                          primary={t(card.titleKey)}
                          primaryTypographyProps={{ fontSize: '0.875rem' }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            );
          })}

          {/* Özel Raporlar sütunu: kullanıcının kurduğu dinamik rapor kartları */}
          {onAddReport && (
            <Box
              sx={{
                flex: 1,
                minWidth: { sm: 220 },
                borderLeft: { xs: 'none', sm: '1px solid' },
                borderTop: { xs: '1px solid', sm: 'none' },
                borderColor: { xs: 'divider', sm: 'divider' },
              }}
            >
              <Typography
                variant="caption"
                sx={{ px: 2, pt: 1.5, display: 'block', fontWeight: 'bold', color: 'text.secondary', textTransform: 'uppercase' }}
              >
                {t('dashboard.cards.groupCustomReports')}
              </Typography>
              <List dense disablePadding>
                {customReports.map((report) => {
                  const visible = !hiddenCards.includes(report.id);
                  return (
                    <ListItem
                      key={report.id}
                      secondaryAction={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {onEditReport && (
                            <IconButton size="small" onClick={() => onEditReport(report)}>
                              <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          )}
                          {onDeleteReport && (
                            <IconButton size="small" onClick={() => onDeleteReport(report)}>
                              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          )}
                          <Switch
                            edge="end"
                            size="small"
                            checked={visible}
                            onChange={() => onToggleCard(report.id)}
                          />
                        </Box>
                      }
                      sx={{ py: 0, pr: 14 }}
                    >
                      <ListItemText
                        primary={report.title}
                        primaryTypographyProps={{ fontSize: '0.875rem', noWrap: true }}
                      />
                    </ListItem>
                  );
                })}
              </List>
              {customReports.length === 0 && (
                <Typography variant="body2" color="textSecondary" sx={{ px: 2, py: 0.5 }}>
                  {t('dashboard.reportBuilder.emptyList')}
                </Typography>
              )}
              <Box sx={{ px: 2, py: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={onAddReport}>
                  {t('dashboard.reportBuilder.newReportCard')}
                </Button>
                {onAskAi && (
                  <Button size="small" variant="outlined" startIcon={<AutoAwesomeIcon />} onClick={onAskAi}>
                    {t('dashboard.reportBuilder.ai.askButton')}
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('dashboard.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DashboardSettingsModal;
